import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	TerraDraw,
	TerraDrawSelectMode,
	TerraDrawPolygonMode,
	TerraDrawLineStringMode,
	TerraDrawFreehandMode,
	TerraDrawPointMode,
	TerraDrawRenderMode,
	type GeoJSONStoreFeatures,
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import type { RefObject } from "react";
import type { MapRef } from "react-map-gl/maplibre";
// FeatureId is declared but not exported from terra-draw
export type FeatureId = string | number;

// Re-export coord cleaning utils for other modules
export { cleanPolygonCoords, findInvalidCoordinates, COORD_PRECISION } from "../../helpers/JobMap/editHelpers";

export type DrawModeName = "select" | "polygon" | "linestring" | "freehand" | "point" | "static";

export type FinishHandler = (id: FeatureId, context: { mode: string; action: string }) => void;
export type ChangeHandler = (ids: FeatureId[], type: string) => void;

export interface MapDrawEngine {
	setMode(mode: DrawModeName): void;
	getSnapshot(): GeoJSONStoreFeatures[];
	clear(): void;
	addFeatures(features: GeoJSONStoreFeatures[]): void;
	removeFeatures(ids: FeatureId[]): void;
	selectFeature(id: FeatureId): void;
	deselectFeature(id: FeatureId): void;
	setFinishHandler(cb: FinishHandler | null): void;
	setChangeHandler(cb: ChangeHandler | null): void;
	isReady: boolean;
}

export interface UseMapDrawEngineOptions {
	mapRef: RefObject<MapRef | null>;
	enabled: boolean;
}

const COORD_PRECISION_ADAPTER = 8;

export function useMapDrawEngine({ mapRef, enabled }: UseMapDrawEngineOptions): MapDrawEngine {
	const drawRef = useRef<TerraDraw | null>(null);
	const [isReady, setIsReady] = useState(false);
	const finishHandlerRef = useRef<FinishHandler | null>(null);
	const changeHandlerRef = useRef<ChangeHandler | null>(null);

	useEffect(() => {
		if (!enabled || !mapRef.current) return;

		const map = mapRef.current.getMap();
		let cancelled = false;

		function teardown() {
			const old = drawRef.current;
			drawRef.current = null;
			setIsReady(false);
			if (old) {
				try {
					old.stop();
				} catch {
					/* layers already gone after setStyle */
				}
			}
		}

		function createDraw(): TerraDraw {
			const adapter = new TerraDrawMapLibreGLAdapter({ map, coordinatePrecision: COORD_PRECISION_ADAPTER });

			const selectMode = new TerraDrawSelectMode({
				flags: {
					polygon: {
						feature: {
							draggable: true,
							coordinates: {
								draggable: true,
								midpoints: { draggable: true },
								deletable: true,
							},
						},
					},
					point: {
						feature: {
							draggable: true,
						},
					},
				},
			});

			const polygonMode = new TerraDrawPolygonMode();
			const lineStringMode = new TerraDrawLineStringMode();
			const freehandMode = new TerraDrawFreehandMode();
			const pointMode = new TerraDrawPointMode();
			const renderMode = new TerraDrawRenderMode({ modeName: "static", styles: {} });

			let counter = 0;
			const idStrategy = {
				isValidId: (id: number | string) => typeof id === "string" && id.length > 0,
				getId: () => `td-${Date.now()}-${counter++}`,
			};

			const draw = new TerraDraw({
				adapter,
				modes: [selectMode, polygonMode, lineStringMode, freehandMode, pointMode, renderMode],
				idStrategy,
			});

			draw.on("finish", (id, context) => finishHandlerRef.current?.(id, context));
			draw.on("change", (ids, type) => changeHandlerRef.current?.(ids, type));
			draw.start();
			draw.setMode("static");
			return draw;
		}

		function init() {
			if (cancelled) return;
			const draw = createDraw();
			drawRef.current = draw;
			setIsReady(true);
		}

		// On style change (tile source swap), MapLibre destroys all layers.
		// We must teardown immediately (before any tool can call stale adapter methods)
		// then re-init once the new style is ready.
		const onStyleLoad = () => {
			if (cancelled) return;
			teardown();
			if (map.isStyleLoaded()) {
				init();
			} else {
				map.once("style.load", init);
			}
		};
		map.on("style.load", onStyleLoad);

		// Initial init
		if (map.isStyleLoaded()) {
			init();
		} else {
			map.once("style.load", init);
		}

		return () => {
			cancelled = true;
			map.off("style.load", onStyleLoad);
			map.off("style.load", init);
			teardown();
		};
	}, [enabled, mapRef]);

	const setMode = useCallback((mode: DrawModeName) => {
		drawRef.current?.setMode(mode);
	}, []);

	const getSnapshot = useCallback((): GeoJSONStoreFeatures[] => {
		return drawRef.current?.getSnapshot() ?? [];
	}, []);

	const clear = useCallback(() => {
		drawRef.current?.clear();
	}, []);

	const addFeatures = useCallback((features: GeoJSONStoreFeatures[]) => {
		drawRef.current?.addFeatures(features);
	}, []);

	const removeFeatures = useCallback((ids: FeatureId[]) => {
		drawRef.current?.removeFeatures(ids);
	}, []);

	const selectFeature = useCallback((id: FeatureId) => {
		drawRef.current?.selectFeature(id);
	}, []);

	const deselectFeature = useCallback((id: FeatureId) => {
		drawRef.current?.deselectFeature(id);
	}, []);

	const setFinishHandler = useCallback((cb: FinishHandler | null) => {
		finishHandlerRef.current = cb;
	}, []);

	const setChangeHandler = useCallback((cb: ChangeHandler | null) => {
		changeHandlerRef.current = cb;
	}, []);

	return useMemo(
		() => ({
			setMode,
			getSnapshot,
			clear,
			addFeatures,
			removeFeatures,
			selectFeature,
			deselectFeature,
			setFinishHandler,
			setChangeHandler,
			isReady,
		}),
		// all callbacks are stable (useCallback []); only isReady changes (once, on init)
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isReady],
	);
}
